/**
 * analytics.js
 *
 * Thin wrapper around the GA4 gtag already loaded in public/index.html.
 * All calls are no-ops if gtag is unavailable (e.g. during tests or when
 * the user has an ad-blocker).
 *
 * lat/lng coordinates are intentionally never sent to GA (GDPR).
 * Location insight comes from GA4's built-in IP-based geolocation.
 */

const send = (eventName, params = {}) => {
  try {
    if (typeof window !== 'undefined' && typeof window.gtag === 'function') {
      window.gtag('event', eventName, params);
    }
  } catch {
    // Never let analytics errors affect the app
  }
};

/**
 * Fire an explicit GA4 page_view event.
 * Must be called AFTER enterMapView / writeCourseStateToUrl so that
 * window.location already reflects the new path and query string.
 * GA4 will see page_path = '/map' or '/' and page_location = full URL
 * (which never contains lat/lng — GDPR safe).
 */
export const trackPageView = (pageTitle) => {
  try {
    if (typeof window !== 'undefined' && typeof window.gtag === 'function') {
      window.gtag('event', 'page_view', {
        page_title:    pageTitle,
        page_location: window.location.href,
        page_path:     window.location.pathname + window.location.search,
      });
    }
  } catch {
    // Never let analytics errors affect the app
  }
};

/** User opened the map view after configuring the course */
export const trackMapViewed = ({ courseType, markCount }) =>
  send('view_map', { course_type: courseType, mark_count: markCount });

/** User pressed "Lay The Course" — course marks calculated successfully */
export const trackCourseCalculated = ({ courseType, windDirection, distance }) =>
  send('lay_course', {
    course_type:    courseType,
    wind_direction: windDirection,
    distance_nm:    distance,
  });

/** User dropped a physical mark at their GPS position */
export const trackMarkDropped = ({ markLabel }) =>
  send('drop_mark', { mark_label: markLabel });

/** User lifted a physical mark back to its ideal position */
export const trackMarkLifted = ({ markLabel }) =>
  send('lift_mark', { mark_label: markLabel });

/** User copied the shareable URL */
export const trackShareLinkCopied = () =>
  send('share_link');

/** User pressed "Set Start Position" */
export const trackStartPositionSet = () =>
  send('set_start_position');

/** User changed the course type in the configuration panel */
export const trackCourseTypeSelected = ({ courseType }) =>
  send('select_course_type', { course_type: courseType });
